"use client";

import {
  FileImageOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import UploadCloud02 from "@untitled-ui/icons-react/build/esm/UploadCloud02";
import { Col, Form, Modal, Row, Select, Space, Tooltip, Upload } from "antd";
import { useState } from "react";
import cnt from "../../../../../services/definedConstant";
import { BOM } from "../../../../../services/definedMethod";
import { apiConfig } from "../../../../customer/customer.config";

const { Dragger } = Upload;

const documentFields = [
  {
    label: "Investor's Application Form",
    name: "investorApplicationForm",
  },
  {
    label: "Executed Bond Purchase Agreement",
    name: "executedBondPurchaseAgreement",
  },
  {
    label: "Identification Documents",
    name: "identificationDocuments",
  },
  {
    label: "Corporate Resolution (If applicable)",
    name: "corporateResolution",
  },
  {
    label: "Tax Clearance Certificate",
    name: "taxClearanceCertificate",
  },
];

const DocumentaryRequirements = ({
  formData,
  updateFormData,
  documentStatusOptions,
  userData,
  form,
  onFinish,
  entityId,
}) => {
  const formValues = Form.useWatch([], form);
  const { session } = userData;
  const [isLoading, setIsLoading] = useState(false);
  const [documentData, setDocumentData] = useState([]);
  const [fileLists, setFileLists] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewIsPdf, setPreviewIsPdf] = useState(false);

  // Function to construct download URL from resourceId
  const constructDownloadUrl = (resourceId) => {
    return `${apiConfig.Clients}/${entityId}/documents/${resourceId}/attachment`;
  };

  const handleUploadChange = (info, name) => {
    let newFileList = [...info.fileList];

    // Ensure successful uploads include the URL for preview
    newFileList = newFileList.map((file) => {
      if (file.response && file.status === "done") {
        // Construct download URL from resourceId
        if (file.response.resourceId) {
          const downloadUrl = constructDownloadUrl(file.response.resourceId);
          file.url = downloadUrl;
          file.thumbUrl = downloadUrl;
          file.preview = downloadUrl;
          file.isPdf = file.type === "application/pdf";
        }
        return file;
      }
      return file;
    });

    setFileLists((prev) => ({
      ...prev,
      [name]: newFileList,
    }));
  };

  const handleValuesChange = (changedValues, allValues) => {
    updateFormData(allValues);
  };

  const getDetailById = () => {
    setIsLoading(true);

    const url = `${apiConfig.Clients}/${entityId}/documents`;

    const body = {
      session,
    };

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        setIsLoading(false);
      } else {
        const objList = res;
        const tempArray = [];

        objList.forEach((element) => {
          tempArray.push({
            id: element?.id,
            title: element?.name,
            description: element?.fileName,
            type: element.type,
          });
        });

        setDocumentData(tempArray);
        setIsLoading(false);
      }
    });
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = file.originFileObj
        ? URL.createObjectURL(file.originFileObj)
        : "";
    }

    const previewUrl = file.url || file.preview;
    const isPdf =
      file.type === "application/pdf" ||
      (file.name && file.name.toLowerCase().endsWith(".pdf"));

    setPreviewImage(previewUrl);
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf("/") + 1)
    );
    setPreviewIsPdf(isPdf);
    setPreviewVisible(true);
  };

  const handlePreviewCancel = () => setPreviewVisible(false);

  const uploadProps = {
    listType: "picture",
    action: `${apiConfig.Clients}/${entityId}/documents`,
    maxCount: 1,
    headers: {
      Authorization: session,
      "Finsys-Platform-Tenantid": "default",
    },
    accept: "image/*,.pdf",
    onSuccess(ret, file) {
      // Construct download URL from resourceId
      if (ret && ret.resourceId) {
        const downloadUrl = constructDownloadUrl(ret.resourceId);

        // Update the file with the constructed URL
        file.url = downloadUrl;
        file.thumbUrl = downloadUrl;
        file.preview = downloadUrl;
        file.isPdf = file.type === "application/pdf";

        // Update the file list for this specific field
        const fieldName = file.data?.name;
        if (fieldName) {
          setFileLists((prev) => {
            const updatedList = (prev[fieldName] || []).map((f) =>
              f.uid === file.uid
                ? {
                    ...f,
                    url: downloadUrl,
                    thumbUrl: downloadUrl,
                    preview: downloadUrl,
                    status: "done",
                    isPdf: file.isPdf,
                  }
                : f
            );
            return { ...prev, [fieldName]: updatedList };
          });
        }
      }

      getDetailById();
    },
    beforeUpload(file) {
      const isJpgOrPng =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "application/pdf";
      if (!isJpgOrPng) {
        BOM.AlertMsg(cnt.DANGER, "You can only upload JPG/PNG or PDF file!");
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        BOM.AlertMsg(cnt.DANGER, "File must be smaller than 5MB!");
        return false;
      }
      return true;
    },
    onPreview: handlePreview,
    iconRender: (file) => {
      const isPdf =
        file.type === "application/pdf" ||
        (file.name && file.name.toLowerCase().endsWith(".pdf"));

      if (file.status === "uploading") {
        return isPdf ? (
          <FilePdfOutlined style={{ fontSize: 24, color: "#BA24D5" }} />
        ) : (
          <FileImageOutlined style={{ fontSize: 24, color: "#BA24D5" }} />
        );
      }

      return null; // Use default icon after upload is done
    },
  };

  const handleFinish = (_data) => {
    onFinish(_data);
  };

  return (
    <div className="form-section">
      <h2 style={{ margin: 0, fontSize: "24px", marginBottom: "24px" }}>
        Document Requirements
      </h2>

      <Form
        form={form}
        onFinish={handleFinish}
        layout="vertical"
        initialValues={formData}
        onValuesChange={handleValuesChange}
        style={{ width: "100%" }}
      >
        <Row>
          {documentFields.map((field) => (
            <Col span={12} style={{ padding: "0px 8px" }} key={field.name}>
              <Form.Item
                label={
                  <Space>
                    {field.label}
                    <Tooltip title="Select document status">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name={field.name}
                rules={[
                  {
                    required: true,
                    message: `Please select ${field.name} status`,
                  },
                ]}
              >
                <Select
                  placeholder="Received/Pending"
                  optionFilterProp="children"
                  style={{ height: "44px" }}
                  options={documentStatusOptions}
                />
              </Form.Item>

              {/* Show Upload when status is 'Received' */}
              {formValues?.[field.name] === 100 && (
                <Form.Item style={{ marginTop: "8px" }}>
                  <Dragger
                    {...{
                      ...uploadProps,
                      data: { name: field.name },
                      onChange: (info) => handleUploadChange(info, field.name),
                      fileList: fileLists[field.name] || [],
                    }}
                    style={{
                      border: "2px dashed #BA24D5",
                      borderRadius: "8px",
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ textAlign: "center", padding: "12px 0" }}>
                      <UploadCloud02 color="#BA24D5" />
                      <p
                        style={{
                          margin: "8px 0",
                          fontWeight: 500,
                          color: "#333",
                        }}
                      >
                        <span style={{ color: "#BA24D5" }}>
                          Click to upload
                        </span>{" "}
                        or drag and drop
                      </p>
                      <p style={{ fontSize: "12px", color: "#999" }}>
                        PDF (max. 5MB)
                      </p>
                    </div>
                  </Dragger>
                </Form.Item>
              )}
            </Col>
          ))}
        </Row>
      </Form>

      {/* Preview Modal */}
      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handlePreviewCancel}
        width={previewIsPdf ? "80%" : "auto"}
        style={{ top: 20 }}
      >
        {previewIsPdf ? (
          <iframe
            src={`${previewImage}#toolbar=0`}
            style={{ width: "100%", height: "70vh" }}
            title={previewTitle}
          />
        ) : (
          <img
            alt={previewTitle}
            style={{ width: "100%" }}
            src={previewImage || "/placeholder.svg"}
          />
        )}
      </Modal>
    </div>
  );
};

export default DocumentaryRequirements;
